import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import StudentSidebar from '../components/StudentSidebar.js';
import '../styles/StudentDashboard.css';
import {
  BookOpen,
  ClipboardList,
  Calendar,
  Clock10,
  GraduationCap,
} from 'lucide-react';
import { roomDetailLines } from '../utils/roomDisplay.js';

/** Ile najbliższych zajęć pokazać w sekcji na panelu (pełna lista jest w kalendarzu). */
const UPCOMING_SESSIONS_ON_DASHBOARD = 5;

export default function StudentDashboardPanel() {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);

  const [tasksData, setTasksData] = useState({
    passedTasks: 0,
    failedTasks: 0,
    totalTasks: {
      total_tasks: 0,
      submitted_tasks: 0,
    },
  });

  const [upcomingCourses, setUpcomingCourses] = useState([]);
  const [upcomingTasks, setUpcomingTasks] = useState([]);

  useEffect(() => {
    async function fetchTasks() {
      try {
        const token = localStorage.getItem('token');

        const tasksResponse = await fetch(
          `${process.env.REACT_APP_API_URL}/student/dashboard/tasks`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (!tasksResponse.ok) {
          throw new Error(`Błąd HTTP: ${tasksResponse.status}`);
        }

        const tasksData = await tasksResponse.json();
        setTasksData(tasksData);
      } catch (error) {
        console.error('Błąd podczas pobierania danych zadań:', error);
      } finally {
        setLoading(false);
      }
    }

    async function fetchUpcomingCourses() {
      try {
        const token = localStorage.getItem('token');

        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/student/dashboard/upcomingCourses`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );

        if (!response.ok) {
          throw new Error(`Błąd HTTP: ${response.status}`);
        }

        const data = await response.json();
        setUpcomingCourses(data);
      } catch (error) {
        console.error('Błąd podczas pobierania nadchodzących kursów:', error);
      }
    }

    async function fetchUpcomingTasks() {
      try {
        const token = localStorage.getItem('token');

        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/student/dashboard/upcomingTasks`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );

        if (!response.ok) {
          throw new Error(`Błąd HTTP: ${response.status}`);
        }

        const data = await response.json();
        setUpcomingTasks(data);
      } catch (error) {
        console.error('Błąd podczas pobierania nadchodzących zadań:', error);
      }
    }

    fetchTasks();
    fetchUpcomingCourses();
    fetchUpcomingTasks();
  }, []);

  const stats = [
    {
      label: 'Zaliczone zadania',
      value: loading ? '…' : tasksData.passedTasks ?? 0,
      icon: BookOpen,
      badge: 'Sukces',
      colorClass: 'stat-success',
    },
    {
      label: 'Nie zaliczone',
      value: loading ? '…' : tasksData.failedTasks ?? 0,
      icon: BookOpen,
      badge: 'Do poprawy',
      colorClass: 'stat-danger',
    },
    {
      label: 'Oddane / wszystkie',
      value: loading
        ? '…'
        : `${tasksData.totalTasks?.submitted_tasks ?? 0}/${tasksData.totalTasks?.total_tasks ?? 0}`,
      icon: ClipboardList,
      badge: 'Postęp',
      colorClass: 'stat-progress',
    },
    {
      label: 'Nadchodzące zajęcia',
      value: upcomingCourses.length,
      icon: Calendar,
      badge: 'Dziś i wkrótce',
      colorClass: 'stat-neutral',
    },
  ];

  // szybkie skróty – ŚCIEŻKI PODMIEŃ POD SWOJE ROUTES
  const quickActions = [
    {
      label: 'Kalendarz zajęć',
      desc: 'Zobacz pełny plan zajęć',
      icon: Calendar,
      path: '/student/schedule',
    },
    {
      label: 'Zadania',
      desc: 'Lista wszystkich zadań',
      icon: ClipboardList,
      path: '/student/tasks',
    },
    {
      label: 'Oceny',
      desc: 'Twoje wyniki i średnie',
      icon: GraduationCap,
      path: '/student/grades',
    },
    {
      label: 'Materiały',
      desc: 'Materiały od wykładowców',
      icon: BookOpen,
      path: '/student/materials',
    },
  ];

  const upcomingCoursesPreview = upcomingCourses.slice(0, UPCOMING_SESSIONS_ON_DASHBOARD);

  return (
    <div className="student-dashboard-panel">
      <StudentSidebar />

      <div className="main-content">
        {/* top bar jak u wykładowcy */}
        <div className="header">
          <h1 className="header-title">Panel główny</h1>
        </div>

        <div className="student-page-inner">
          {/* HEADER STRONY */}
          <header className="student-header-main">
            <div>
              <h2 className="student-header-title">Panel kursanta</h2>
              <p className="student-header-subtitle">
                Śledź swoje zaliczenia, zadania i nadchodzące zajęcia w jednym miejscu.
              </p>
            </div>
          </header>

          {/* GÓRNY RZĄD STATYSTYK */}
          <section className="student-stats-row">
            {stats.map((stat, idx) => {
              const Icon = stat.icon;
              return (
                <article key={idx} className="student-stat-card">
                  <div className="student-stat-icon">
                    <Icon size={20} />
                  </div>
                  <div className="student-stat-body">
                    <p className={`student-stat-value ${stat.colorClass}`}>
                      {stat.value}
                    </p>
                    <p className="student-stat-label">{stat.label}</p>
                    <span className="student-stat-badge">{stat.badge}</span>
                  </div>
                </article>
              );
            })}
          </section>

          {/* SZYBKIE SKRÓTY */}
          <section className="student-quick-actions">
            {quickActions.map((action, idx) => {
              const Icon = action.icon;
              return (
                <button
                  key={idx}
                  type="button"
                  className="student-quick-action-btn"
                  onClick={() => navigate(action.path)}
                >
                  <div className="student-quick-icon">
                    <Icon size={18} />
                  </div>
                  <div className="student-quick-text">
                    <span className="student-quick-label">{action.label}</span>
                    <span className="student-quick-desc">{action.desc}</span>
                  </div>
                </button>
              );
            })}
          </section>

          {/* GŁÓWNY GRID: ZAJĘCIA + ZADANIA */}
          <div className="student-main-grid">
            {/* Nadchodzące zajęcia */}
            <section className="student-card student-card-wide">
              <h2 className="student-card-header">
                <BookOpen size={20} />
                <span>Nadchodzące zajęcia</span>
              </h2>

              {upcomingCourses.length === 0 ? (
                <p className="student-empty">
                  Brak zaplanowanych zajęć w najbliższym czasie.
                </p>
              ) : (
                <ul className="student-upcoming-list">
                  {upcomingCoursesPreview.map((course) => (
                    <li key={course.session_id} className="student-upcoming-item">
                      <div className="student-up-main">
                        <h3 className="student-up-title">{course.module_name}</h3>
                        {course.title && (
                          <p className="student-up-subtitle">{course.title}</p>
                        )}
                        <p className="student-up-lecturer">
                          {course.lecturer || '---'}
                        </p>
                        {course.description && (
                          <p className="student-up-desc">{course.description}</p>
                        )}
                      </div>
                      <div className="student-up-meta">
                        <span className="student-chip">
                          <Calendar size={14} />
                          <span>{course.session_date}</span>
                        </span>
                        <span className="student-chip">
                          <Clock10 size={14} />
                          <span>
                            {course.start_time} – {course.end_time}
                          </span>
                        </span>
                        <div className="student-room-block">
                          {roomDetailLines(
                            course.room_name,
                            course.location,
                            course.room_comment
                          ).map((line, i) => (
                            <span key={i} className="student-meta-inline">
                              {line.label}: <strong>{line.value}</strong>
                            </span>
                          ))}
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </section>

            {/* Zadania */}
            <section className="student-card">
              <h2 className="student-card-header">
                <ClipboardList size={20} />
                <span>Twoje zadania</span>
              </h2>

              {upcomingTasks.length === 0 ? (
                <p className="student-empty">Nie masz jeszcze przypisanych zadań.</p>
              ) : (
                <ul className="student-tasks-list">
                  {upcomingTasks.map((task) => (
                    <li
                      key={task.assignment_id}
                      className="student-task-item"
                    >
                      <div className="student-task-main">
                        <p className="student-task-title">
                          {task.nazwa_zadania || 'Zadanie bez nazwy'}
                        </p>
                        {task.opis_zadania && (
                          <p className="student-task-desc">
                            {task.opis_zadania}
                          </p>
                        )}
                      </div>
                      <div className="student-task-meta">
                        <span className="student-task-deadline">
                          Termin:{' '}
                          <strong>{task.termin_oddania || '---'}</strong>
                        </span>
                        {task.oddane && (
                          <span className="student-task-status">
                            Status: {task.oddane}
                          </span>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          </div>
        </div>
      </div>
    </div>
  );
}
